/*
 * @Author: Ryan
 * @Date: 2026-03-11
 * @Description: How to Create Your AI Twin - 4-step workflow
 */
import Image from 'next/image';
import React from 'react';

interface StepCardProps {
  index: number;
  title: string;
  description: string;
}

const steps = [
  {
    title: 'Choose Your Photo',
    description:
      'Upload a clear photo of yourself so the AI can build an accurate, natural-looking digital twin for your video. A high-quality image helps produce a more realistic final result.',
  },
  {
    title: 'Write What You Want It to Say',
    description:
      'Type the lines you want your AI twin to speak, and the system automatically formats your text for smooth delivery. You can adjust tone or pacing before moving to the next step.',
  },
  {
    title: 'Pick the Perfect Voice',
    description:
      'Choose an AI voice or use the recorded audio to give your digital twin a realistic, expressive sound. Selecting the right voice helps your AI twin feel more authentic.',
  },
  {
    title: 'Generate Your AI Twin Video',
    description:
      'Click Start and let the AI create a fully lip-synced HD video of your digital twin. The system blends your image, script, and voice into a seamless talking avatar. Once complete, you can download, edit, or share your video instantly.',
  },
];

const StepCard: React.FC<StepCardProps> = ({ index, title, description }) => {
  return (
    <div className="flex gap-4 rounded-2xl p-4 pad:p-5">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6366F1] text-white flex items-center justify-center font-semibold text-sm">
        {index}
      </div>
      <div>
        <h3 className="text-base pad:text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-xs pad:text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  );
};

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-8 text-center">
          How to Create Your AI Twin
        </h2>

        <div className="max-w-[1010px] mx-auto flex flex-col sm:flex-row items-center gap-8 pad:gap-10 sm:gap-12">
          {/* Visual */}
          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden">
              <Image
                src="https://assets.falcocut.ai/tools/tiktok-video-generator/how-to.webp"
                alt="How to create your AI Twin with FalcoCut"
                width={440}
                height={520}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
          {/* Steps */}
          <div className="flex-1 w-full">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <StepCard
                  key={step.title}
                  index={index + 1}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
